<template>
  <div class="animated fadeIn">

    <!-- We are a new user, get started instructions -->
    <div v-if="needsSomething">
      <b-card>
        <h2>Welcome, {{$session.get('firstName')}}</h2>
        <p class="lead mt-2">Welcome to AdReplay! We're glad to have you on board! These simple steps will help get you up and running as quickly as possible.</p>
        <div class="lead">Need help? Our team is here to help you in any way we can. <b-link to="/support">Send us an email</b-link> and we'll be in touch shortly.</div>
      </b-card>

      <b-row class="mt-2">
        <b-col :md="8" :offset-md="2" :lg="4" :offset-lg="0" class="d-flex">
          <b-card class="w-100" body-class="d-flex flex-grow-1 flex-column justify-content-between text-center">
            <div>
                <i class="icon-globe font-3xl text-primary d-block mt-3"></i>
                <h4 class="mt-3">Link Publisher Account</h4>
                <p>Connect to your Kijiji account to activate posting</p>
            </div>
            <div class="mt-2">
              <b-button v-if="needsPublisher" to="/publishers" size="lg" variant="primary">Let's Go</b-button>
              <div v-else class="d-inline-block text-success step-complete"><i class="cui-circle-check"></i></div>
            </div>
          </b-card>
        </b-col>
        <b-col :md="8" :offset-md="2" :lg="4" :offset-lg="0" class="d-flex">
          <b-card class="w-100" body-class="d-flex flex-grow-1 flex-column justify-content-between text-center">
            <div>
                <i class="icon-layers font-3xl text-primary d-block mt-2"></i>
                <h4 class="mt-3">Create Ad Listings</h4>
                <p>Create advertisements and schedule them for automatic publishing</p>
            </div>
            <div class="mt-2">
              <b-button v-if="needsListing" to="/listings/new" size="lg" variant="primary">Show Me How</b-button>
              <div v-else class="d-inline-block text-success step-complete"><i class="cui-circle-check"></i></div>
            </div>
          </b-card>
        </b-col>
        <b-col :md="8" :offset-md="2" :lg="4" :offset-lg="0" class="d-flex">
          <b-card class="w-100" body-class="d-flex flex-grow-1 flex-column justify-content-between text-center">
            <div>
              <i class="icon-globe font-3xl text-primary"></i>
              <h4 class="mt-3">Connect Billing</h4>
              <p>Link your billing information now to activate your free trial and start posting</p>
            </div>
            <div class="mt-2">
              <b-button v-if="needsBilling" to="/account" size="lg" variant="primary">Get Connected</b-button>
              <div v-else class="d-inline-block text-success step-complete"><i class="cui-circle-check"></i></div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- We are up and running, provide shortcuts -->
    <div v-else>
      <all-listings class="mt-2" />

      <b-row>
        <b-col :sm="4">
          <dashboard-kijiji />
        </b-col>
        <b-col :sm="4">
          <div class="brand-card" data-block-disabled="Coming Soon!">
            <div class="brand-card-header flex-column">
              <img src="/img/publishers/craigslist.png" height="50" alt="Kijiji" />
              <div class="mt-1 text-muted">past 30 days</div>
            </div>
            <div class="brand-card-body">
              <div>
                <div class="text-value">0</div>
                <div class="text-uppercase text-muted small">views</div>
              </div>
              <div>
                <div class="text-value">0</div>
                <div class="text-uppercase text-muted small">replies</div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- @TODO assess whether this is even needed anymore... by this point customers know their way around -->
      <b-row v-if="false">
        <b-col :md="8" :offset-md="2" :lg="4" :offset-lg="0" class="d-flex">
          <b-card class="w-100" body-class="d-flex flex-grow-1 flex-column justify-content-between text-center">
            <div>
                <i class="icon-globe font-3xl text-primary d-block mt-3"></i>
                <h4 class="mt-3">Change Publisher Accounts</h4>
                <p>Change your publisher account settings or authorize a new publisher account</p>
            </div>
            <div class="mt-2">
              <b-button to="/publishers" size="lg" variant="primary">Publisher Settings</b-button>
            </div>
          </b-card>
        </b-col>
        <b-col :md="8" :offset-md="2" :lg="4" :offset-lg="0" class="d-flex">
          <b-card class="w-100" body-class="d-flex flex-grow-1 flex-column justify-content-between text-center">
            <div>
                <i class="icon-layers font-3xl text-primary d-block mt-2"></i>
                <h4 class="mt-3">Manage Ad Listings</h4>
                <p>Manage listings or create new ones and schedule them for automatic publishing</p>
            </div>
            <div class="mt-2">
              <b-button to="/listings" size="lg" variant="primary">Manage Listings</b-button>
            </div>
          </b-card>
        </b-col>
        <b-col :md="8" :offset-md="2" :lg="4" :offset-lg="0" class="d-flex">
          <b-card class="w-100" body-class="d-flex flex-grow-1 flex-column justify-content-between text-center">
            <div>
              <i class="icon-globe font-3xl text-primary"></i>
              <h4 class="mt-3">Update My Account</h4>
              <p>Update your account information or change your subscription plan</p>
            </div>
            <div class="mt-2">
              <b-button to="/account" size="lg" variant="primary">My Account</b-button>
            </div>
          </b-card>
        </b-col>
      </b-row>

    </div>
  </div>
</template>

<script>
import AllListings from "./dashboard/AllListings"
import DashboardKijiji from './dashboard/Kijiji'

export default {
  name: 'Dashboard',

  components: {
    AllListings,
    DashboardKijiji,
  },

  computed: {
    needsSomething(){
      return this.needsPublisher || this.needsListing || this.needsBilling
    }
  },

  data() {
    return {
      needsPublisher : this.$session.get('dashboard.publisher'),
      needsListing   : this.$session.get('dashboard.listing'),
      needsBilling   : this.$session.get('dashboard.billing'),
    };
  },

  methods: {}
};
</script>

<style lang="scss" scoped>
.step-complete {
  i {
    font-size: 3rem;
  }
}
</style>
